import React, { useEffect, useRef, useState } from 'react';

// Utils
import { sortBy, truncate } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useAppContext } from '../../../../store/AppContext';
import PermissionsGate, { isGranted } from '../../../../utils/gate/permissionsGate';
import SCOPE from '../../../../utils/gate/scope';
import cn from '../../../../utils/tools/cn';
import useModal from '../../../../utils/hooks/useModal';
import useImage from '../../../../utils/hooks/useImage';

// Icons
import { ReactComponent as TrashIcon } from '../../../../assets/icons/trash.svg';
import { ReactComponent as ViewIcon } from '../../../../assets/icons/eye.svg';

// Services
import useMediaService from '../../../../services/mediaService';

// Components
import FlashMessage from '../../../molecules/FlashMessage/FlashMessage';
import SkeletonLoader from '../../../molecules/SkeletonLoader/SkeletonLoader';
import EmptyList from '../../../organisms/EmptyList/EmptyList';
import MediaEdition from './MediaEdition/MediaEdition';

import styles from './MediaLibrary.module.scss';
import AddNewMedia from './AddNewMedia/AddNewMedia';

function MediaLibrary() {
  const { t } = useTranslation();
  const [{ user }, stateDispatch] = useAppContext();

  const { displayModal, closeModal } = useModal();
  const { getImageSrc } = useImage();

  const mediaEditionForm = useRef();

  const {
    getMedias, deleteMedia,
  } = useMediaService();

  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSuccess, setUploadSuccess] = useState([]);

  const [medias, setMedias] = useState([]);

  const { isLoading } = useQuery('getMedias', getMedias, {
    onSuccess: (res) => {
      if (res?.data?.medias) {
        setMedias(res.data.medias);
      }
    },
  });

  const {
    mutate: deleteMediaMutate,
    isLoading: deleteMediaLoading,
  } = useMutation((media) => deleteMedia(media.id), {
    onSuccess: (_, data) => {
      closeModal();
      if (data) {
        setMedias((state) => state.filter((media) => media.id !== data.id));
      }

      setUploadSuccess((state) => state.concat([t('medias.delete.success', { name: data.name })]));
    },
    onError: (_, data) => {
      closeModal();
      setUploadErrors((state) => state.concat([t('medias.delete.error', { name: data.name })]));
    },
  });

  useEffect(() => {
    stateDispatch({ type: 'SET_MODAL_QUERY_LOADING', payload: deleteMediaLoading ? 'deleteMedia' : null });
  }, [deleteMediaLoading]);

  const handleDeleteMedia = (media) => {
    displayModal({
      maxWidth: 520,
      title: t('medias.delete.title'),
      content: (
        <p dangerouslySetInnerHTML={{ __html: t('medias.delete.text', { name: media.name }) }} />
      ),
      buttons: [
        {
          label: t('common.cancel'),
          theme: 'inverted',
        },
        {
          label: t('medias.delete.confirm'),
          onClick: () => deleteMediaMutate(media),
          queryName: 'deleteMedia',
        },
      ],
    });
  };

  const handleEditMedia = (media) => {
    displayModal({
      maxWidth: 1020,
      title: t('medias.edition.title'),
      coverImage: (
        <img
          alt={media.name}
          src={getImageSrc({
            fileId: media.fileId,
            widths: {
              default: 475,
            },
          })}
        />
      ),
      content: (
        <MediaEdition
          ref={mediaEditionForm}
          media={media}
          handleDeleteMedia={handleDeleteMedia}
        />
      ),
    });
  };

  return (
    <div>
      {uploadErrors?.length ? (
        <div className="d-flex d-col m-b-30">
          {uploadErrors.map((error, index) => (
            <FlashMessage className="m-b-5" key={index} message={error} color="error" />
          ))}
        </div>
      ) : null}

      {uploadSuccess?.length ? (
        <div className="d-flex d-col m-b-30">
          {uploadSuccess.map((error, index) => (
            <FlashMessage className="m-b-5" key={index} message={error} color="success" />
          ))}
        </div>
      ) : null}

      {isLoading ? (
        <div className={styles.medias_grid}>
          {Array.from(Array(10).keys()).map((_, index) => (
            <div className={styles.media_item} key={index}>
              <SkeletonLoader className={styles.media_img} radius={20} />
              <SkeletonLoader height={20} className="m-t-10" />
            </div>
          ))}
        </div>
      ) : (
        medias?.length ? (
          <ul className={styles.medias_grid}>
            {sortBy(medias, 'name').map((media, index) => (
              <li key={index} className={styles.media_item}>
                <div className={cn([styles.media_img, styles.media_img_loaded])}>
                  <img
                    alt={media.name}
                    src={getImageSrc({
                      fileId: media.fileId,
                      widths: {
                        default: 336,
                        smallMobile: 128,
                        mobile: 324,
                        tablet: 287,
                        smallLaptop: 255,
                      },
                    })}
                  />

                  <div className={cn([styles.media_actions, 'd-flex d-col center'])}>
                    <PermissionsGate scopes={[SCOPE.delete_pla_media, SCOPE.delete_ownEst_media]}>
                      <button
                        type="button"
                        onClick={() => handleDeleteMedia(media)}
                        className={cn(['m-l-auto p-10 c-pointer', styles.delete])}
                      >
                        <TrashIcon width={16} height={16} />
                      </button>
                    </PermissionsGate>

                    <button
                      type="button"
                      onClick={() => handleEditMedia(media)}
                      className="d-flex d-col center c-pointer"
                    >
                      <ViewIcon className="m-b-5" />
                      <span className="mobile-fs-12">{t('medias.edition.show')}</span>
                    </button>
                  </div>
                </div>

                <p className="m-t-15 fs-14 fw-300">{truncate(media.name, { length: 60 })}</p>

                {media.size ? (
                  <p className="m-t-4 fs-14 fw-500">{`${Math.round(media.size / 1000)} Ko`}</p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyList
            imageTheme="medias"
            title={t('medias.empty.title')}
            text={`
              ${t('medias.empty.text')} 
              ${isGranted(user, [SCOPE.create_pla_media, SCOPE.create_ownEst_media])
              ? t('medias.empty.add')
              : ''}
            `}
          />
        )
      )}

      {!isLoading ? (
        <PermissionsGate scopes={[SCOPE.create_pla_media, SCOPE.create_ownEst_media]}>
          <div className="m-t-20 d-flex a-center j-end sticky-actions mobile-a-stretch">
            <AddNewMedia
              setUploadErrors={setUploadErrors}
              setUploadSuccess={setUploadSuccess}
              setMedias={setMedias}
              displayLimit
              buttonLabel={t('medias.upload.add')}
              scrollToTopOnUpload
            />
          </div>
        </PermissionsGate>
      ) : null}
    </div>
  );
}

MediaLibrary.propTypes = {};

export default MediaLibrary;
